import React from 'react';

import {useTranslation} from 'common/i18n/hooks';
import {Trans, TranslationKey} from 'common/i18n';
import {ButtonTheme} from 'common/components/button/constants';
import {ButtonReact} from 'common/components/button';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {getReportUrl} from 'common/report/util';
import UpsellImage from 'common/assets/images/session-replay/session-replay-upsell.png';

import {EmbedErrorStateType, ReplayErrorStateType} from '../types';
import styles from './index.module.styl';

export type SessionRecordingErrorStateProps = {
  titleKey: TranslationKey;
  subtitleKey: TranslationKey;
  subtitleLink: string;
  type?: ReplayErrorStateType | EmbedErrorStateType;
  error?: string;
};

export function SessionRecordingErrorState(props: SessionRecordingErrorStateProps) {
  const {t} = useTranslation();
  const currentProject = useCurrentProject().get().value;
  const docsLink = `https://docs.mixpanel.com/docs/session-replay#implementation`;
  const salesLink = `https://mixpanel.com/contact-us/sales/`;
  const trackEventName = `Session Replay Home Upsell`;
  const selfServeUrl = currentProject?.id
    ? getReportUrl(
        `settings#org/${currentProject?.organizationId}/plan?show-modal=plan-builder&pb-step=session-replay`,
        {
          projectId: currentProject!.id,
        },
      )
    : null;

  return (
    <div className={styles.noDataState}>
      {props.type && <img className={styles.upsellImage} src={UpsellImage} />}
      <div className={styles.upsellTitle}>{t(props.titleKey)}</div>
      {props.type === ReplayErrorStateType.NotIntegrated ? (
        <div className={styles.upsellDesc}>
          <li>{t(`session-replay.home-upsell-desc-1`)}</li>
          <li>{t(`session-replay.home-upsell-desc-2`)}</li>
          <li>{t(`session-replay.home-upsell-desc-3`)}</li>
        </div>
      ) : (
        <>
          <div className={styles.emptyStateSubtitle}>
            <Trans
              t={t}
              i18nKey={props.subtitleKey}
              components={{
                docsLink: <a href={props.subtitleLink} target={`_blank`} />,
              }}
            />
          </div>
          {props.error && <div className={styles.emptyStateSubtitle}>{props.error}</div>}
        </>
      )}
      <div className={styles.upsellButtonContainer}>
        {props.type === ReplayErrorStateType.NotIntegrated ? (
          <>
            <ButtonReact theme={ButtonTheme.Primary} onClick={() => window.open(docsLink, `_blank`)}>
              {t(`session-replay.home-upsell-self-serve`)}
            </ButtonReact>
            <ButtonReact
              onClick={() => {
                mixpanel.track(trackEventName, {link: `Contact Sales`});
                window.open(salesLink, `_blank`);
              }}
            >
              {t(`session-replay.home-upsell-contact`)}
            </ButtonReact>
          </>
        ) : (
          <>
            <ButtonReact theme={ButtonTheme.Primary} onClick={() => window.open(docsLink, `_blank`)}>
              {t(`session-replay.home-view-docs`)}
            </ButtonReact>
            <ButtonReact
              onClick={() => {
                if (selfServeUrl) {
                  mixpanel.track(trackEventName, {link: `Upgrade Plan`});
                  window.open(selfServeUrl, `_blank`);
                }
              }}
            >
              {t(`session-replay.home-upgrade-plan`)}
            </ButtonReact>
          </>
        )}
      </div>
      {props.type === ReplayErrorStateType.NotIntegrated && (
        <div className={styles.troubleshootingText}>
          <Trans
            t={t}
            i18nKey={props.subtitleKey}
            components={{
              docsLink: <a href={docsLink} target={`_blank`} />,
            }}
          />
        </div>
      )}
    </div>
  );
}
